import XDate from 'xdate'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBuilding,
  faBuildingColumns,
  faCity,
  faHouse,
  faHouseChimney,
  faHouseChimneyWindow,
  faWandMagicSparkles,
} from '@fortawesome/pro-duotone-svg-icons'

import { ChoiceTileOption } from '../../components/choice-tile/types'
import { PropertyType } from '../../_types/property'
import {
  DiscoverFilters,
  DiscoverListing,
} from '../../_types/quickpass-discover'
import {
  QuickPassDwellingScope,
  QuickPassProfile,
} from '../../_types/quickpass-profile'

const DEFAULT_PAGE_SIZE = 24

const PROPERTY_TYPE_OPTIONS: {
  value: PropertyType
  label: string
  icon: IconDefinition
}[] = [
  { value: 'apartment', label: 'Apartment', icon: faBuilding },
  { value: 'condo', label: 'Condo', icon: faCity },
  { value: 'house', label: 'House', icon: faHouse },
  { value: 'townhouse', label: 'Townhouse', icon: faHouseChimneyWindow },
]

const BEDROOM_FILTER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Studio' },
  { value: 1, label: '1 bed' },
  { value: 2, label: '2 beds' },
  { value: 3, label: '3 beds' },
  { value: 4, label: '4+ beds' },
]

const BATHROOM_FILTER_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '1 bath' },
  { value: 2, label: '2 baths' },
  { value: 3, label: '3+ baths' },
]

const DWELLING_SCOPE_OPTIONS: ChoiceTileOption<QuickPassDwellingScope>[] = [
  {
    value: 'no-preference',
    title: 'Any',
    description: 'Open to whole places + units',
    icon: faWandMagicSparkles,
  },
  {
    value: 'whole-property',
    title: 'Whole place',
    description: 'Just houses + townhouses',
    icon: faHouseChimney,
  },
  {
    value: 'unit',
    title: 'Unit',
    description: 'Apartments + condos',
    icon: faBuildingColumns,
  },
]

const buildDefaultFilters = (): DiscoverFilters => {
  return {
    cities: [],
    priceMin: null,
    priceMax: null,
    bedroomCounts: [],
    bathroomCounts: [],
    propertyTypes: [],
    dwellingScope: 'no-preference',
    availabilityFilter: 'available-now',
    searchText: '',
  }
}

/**
 * Pre-fills the discover filter bar from the renter's profile so a new
 * browse starts personalised. Returns the empty default when no profile
 * exists yet.
 *
 * @param {QuickPassProfile | null} profile
 * @returns {DiscoverFilters}
 */
const buildFiltersFromProfile = (
  profile: QuickPassProfile | null,
): DiscoverFilters => {
  if (profile === null) {
    return buildDefaultFilters()
  }

  const preferences = profile.unitPreferences

  return {
    cities: [...preferences.cities],
    priceMin: preferences.priceMin,
    priceMax: preferences.priceMax,
    bedroomCounts: [...preferences.bedroomCounts],
    bathroomCounts: [...preferences.bathroomCounts],
    propertyTypes: [...preferences.propertyTypes],
    dwellingScope: preferences.dwellingScope,
    availabilityFilter: 'available-now',
    searchText: '',
  }
}

/**
 * Counts how many filters the renter has actively applied. Useful for the
 * "Reset filters" button visibility and the active-filter badge.
 *
 * @param {DiscoverFilters} filters
 * @returns {number}
 */
const countActiveFilters = (filters: DiscoverFilters): number => {
  let count = 0
  if (filters.cities.length > 0) count += 1
  if (filters.priceMin !== null || filters.priceMax !== null) count += 1
  if (filters.bedroomCounts.length > 0) count += 1
  if (filters.bathroomCounts.length > 0) count += 1
  if (filters.propertyTypes.length > 0) count += 1
  if (filters.dwellingScope !== 'no-preference') count += 1
  if (filters.availabilityFilter !== 'available-now') count += 1
  if (filters.searchText.trim() !== '') count += 1
  return count
}

/**
 * Formats the cost range for display on listing cards. Handles single-
 * value + range cases. Returns "Price on request" when both bounds are
 * zero.
 *
 * @param {DiscoverListing} listing
 * @returns {string}
 */
const formatPriceLabel = (listing: DiscoverListing): string => {
  const formatNumber = (value: number): string =>
    `$${value.toLocaleString('en-CA')}`

  if (listing.costLowest > 0 && listing.costHighest > 0) {
    if (listing.costLowest === listing.costHighest) {
      return `${formatNumber(listing.costLowest)} / month`
    }
    return `${formatNumber(listing.costLowest)} – ${formatNumber(listing.costHighest)} / month`
  }

  if (listing.costLowest > 0) {
    return `${formatNumber(listing.costLowest)} / month`
  }

  if (listing.costHighest > 0) {
    return `${formatNumber(listing.costHighest)} / month`
  }

  return 'Price on request'
}

/**
 * One-line summary of beds / baths / sqft suitable for a card subtitle.
 *
 * @param {DiscoverListing} listing
 * @returns {string}
 */
const formatBedsBathsLine = (listing: DiscoverListing): string => {
  const parts: string[] = []

  if (listing.bedrooms === 0) {
    parts.push('Studio')
  } else if (listing.bedrooms > 0) {
    parts.push(
      `${listing.bedrooms.toString()} bed${listing.bedrooms === 1 ? '' : 's'}`,
    )
  }

  if (listing.bathrooms > 0) {
    parts.push(
      `${listing.bathrooms.toString()} bath${listing.bathrooms === 1 ? '' : 's'}`,
    )
  }

  if (listing.squareFootage > 0) {
    parts.push(`${listing.squareFootage.toLocaleString('en-CA')} ft²`)
  }

  return parts.join(' · ')
}

/**
 * Friendly relative-or-absolute label for the earliest move-in date.
 *
 * @param {DiscoverListing} listing
 * @returns {string}
 */
const formatMoveInLabel = (listing: DiscoverListing): string => {
  if (listing.earliestMoveInTimestamp <= 0) {
    return ''
  }

  const target = new XDate(listing.earliestMoveInTimestamp)
  const now = new XDate()

  if (target.diffDays(now) > 0) {
    return 'Available now'
  }

  return `Available ${target.toString('MMM d')}`
}

export {
  DEFAULT_PAGE_SIZE,
  PROPERTY_TYPE_OPTIONS,
  BEDROOM_FILTER_OPTIONS,
  BATHROOM_FILTER_OPTIONS,
  DWELLING_SCOPE_OPTIONS,
  buildDefaultFilters,
  buildFiltersFromProfile,
  countActiveFilters,
  formatPriceLabel,
  formatBedsBathsLine,
  formatMoveInLabel,
}
